import './VodVideoPlayer.scss';

import React, {useState, useContext, useRef} from 'react';

import Conf from 'utils/Conf';
import Controls from 'components/Controls/Controls';
import Context from 'utils/Context';
import Loader from 'components/Loader';
import Vimeo from '@u-wave/react-vimeo';
import moment from 'moment';
import momentDurationFormatSetup from 'moment-duration-format';

import usePersistentState from '../hooks/usePersistentState';

momentDurationFormatSetup(moment);

const VodVideoPlayer = ({video}) => {
  const context = useContext(Context);
  const {appState} = context;

  const [actuallyPaused, setActuallyPaused] = useState(true);
  const [paused, setPause] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(null);
  const [ready, setReady] = useState(false);
  const [show, setShow] = useState(false);
  const [showLoader, setShowLoader] = useState(null);

  const [timePosition, setTimePosition] = usePersistentState(`vod-${video}-timecode`, null);

  const player = useRef(null);
  const playerRef = useRef(null);
  const stateTimeout = useRef(0);

  // display controls
  const handleMouseMove = () => {
    clearTimeout(stateTimeout.current);
    if (!show) {
      setShow(true);
    }
    stateTimeout.current = setTimeout(() => {
      setShow(false);
    }, Conf.timeBeforeFade);
  };
  const handleMouseLeave = e => {
    clearTimeout(stateTimeout.current);
    setShow(false);
  };

  // play button
  const clickPlayPause = (e, val) => {
    if (e) e.stopPropagation();
    if (val === 'toggle') setPause(!paused);
    else if (val === 'pause') setPause(true);
    else if (val === 'play') setPause(false);
  };
  const onPlayHandler = () => {
    setPause(false);
    setActuallyPaused(false);
  };
  const onPauseHandler = () => {
    setPause(true);
    setActuallyPaused(true);
  };

  // sound button
  const handleMute = e => {
    if (e) e.stopPropagation();
    context.methods.setAppState({volume: 0});
  };
  const handleUnMute = e => {
    if (e) e.stopPropagation();
    context.methods.setAppState({volume: appState.currentVolume});
  };
  const handleVolumeChange = v => {
    context.methods.setAppState({volume: v, currentVolume: v});
  };

  // init phase
  const handleReady = playerArg => {
    player.current = playerArg;
    Promise.all([playerArg.getVideoWidth(), playerArg.getVideoHeight(), playerArg.getDuration()]).then(infos => {
      setReady(true);
      setDuration(infos[2]);
    });
  };

  const handlePositionUpdate = (p, shouldMultiply = true) => {
    const properTime = shouldMultiply ? p * duration : p;
    clickPlayPause(null, 'pause');
    setCurrentTime(properTime);
    setShowLoader(true);
    player.current.setCurrentTime(Math.min(duration - 5, Math.min(properTime))).then(() => {
      setShowLoader(false);
      if (!paused) clickPlayPause(null, 'play');
    });
  };

  const onEndHandler = () => {
    setPause(true);
    player.setCurrentTime(0);
  };

  const onFullScreen = () => {
    playerRef.current.querySelector('div[data-vimeo-initialized] > div').style = {
      padding: 0,
      height: '100vh',
    };
  };

  const handleTimeUpdate = seconds => {
    setCurrentTime(seconds);
    if (seconds > 300 && Math.abs(seconds - timePosition) > 100) {
      setTimePosition(seconds);
    }
  };

  return (
    <div className="vod-videoplayer-wrapper">
      <div
        ref={playerRef}
        className="vod-videoplayer"
        onMouseDown={handleMouseMove}
        onMouseMove={handleMouseMove}
        onMouseLeave={handleMouseLeave}
      >
        <Vimeo
          video={`https://vimeo.com/${video}`}
          controls={false}
          paused={paused}
          volume={appState?.volume}
          onPause={onPauseHandler}
          onPlay={onPlayHandler}
          onReady={handleReady}
          onEnd={onEndHandler}
          onTimeUpdate={e => {
            handleTimeUpdate(e.seconds);
          }}
          className="vodVimeo"
          autoplay
          responsive
        />
        <Loader className="vod-videoplayer-loader" loading={(!paused && actuallyPaused) || showLoader} />
        {ready && (
          <div
            onClick={e => clickPlayPause(e, 'toggle')}
            className={`vod-videoplayer-controls ${show || paused ? 'show' : 'hide'}`}
          >
            <Controls
              currentTime={currentTime}
              actuallyPaused={actuallyPaused}
              duration={duration}
              volume={appState?.volume}
              paused={paused}
              onPlay={e => clickPlayPause(e, 'play')}
              onPause={e => clickPlayPause(e, 'pause')}
              onMute={handleMute}
              onUnMute={handleUnMute}
              onPositionUpdate={handlePositionUpdate}
              onVolumeChange={handleVolumeChange}
              moveDisabled={false}
              fullScreenDisabled={false}
              onFullScreen={onFullScreen}
              direct
              directMin={0}
              node={playerRef.current}
              visibleTimestamp
              timePosition={timePosition}
              withTooltip
            />
          </div>
        )}
      </div>
    </div>
  );
};

export default VodVideoPlayer;
